import { useSubmitSubscriptionChange } from '@app/shared/hooks/subscription-changes.hook';
import { useModulePaywall } from '@app/shared/hooks/use-module-paywall.hook';
import { useAuthStore } from '@app/shared/store/auth.store';
import { formatToCurrencyString } from '@app/shared/utils/format';
import { i18n } from '@i18n/translations';
import {
  Button,
  Card,
  CardContent,
  ModalDialog,
  Skeleton,
} from '@neogeniux/uikit';
import { ArrowRight, Lock, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

interface Props {
  /**
   * Catalog SKU code the page is gated by — e.g. `module.accounting_full`
   * or `addon.ecf`. Looked up against `/public/catalog/sellable-items`
   * to render name + price; against `/subscription/me/items` to decide
   * `hasAccess`.
   */
  sku: string;
  /**
   * Optional override for the catalog name. Defaults to the catalog
   * label when omitted.
   */
  titleOverride?: string;
  /**
   * Optional short pitch to render under the card title. Defaults to
   * the catalog description.
   */
  pitch?: string;
  /**
   * Feature bullets to surface as the value proposition. Caller
   * supplies free-form Spanish strings — no i18n coupling so callers
   * can localize per page.
   */
  features?: string[];
}

/**
 * Drop-in replacement for a page body when the business doesn't own
 * the gating SKU. Renders an upsell card with name + monthly price +
 * "Agregar" CTA → inline confirm dialog → POSTs to
 * `/api/subscription-changes/me`. No redirect to a separate page.
 *
 * Usage:
 *
 *   const gate = useModulePaywall('module.accounting_full');
 *   if (gate.isLoading) return <Skeleton …/>;
 *   if (!gate.hasAccess) return <ModulePaywall sku={gate.sku} … />;
 *   // existing page content
 */
export function ModulePaywall({ sku, titleOverride, pitch, features }: Props) {
  const { t } = useTranslation();
  const { name, monthlyPriceUsd, currency, description, isLoading } =
    useModulePaywall(sku);
  const user = useAuthStore((state) => state.user);
  const submit = useSubmitSubscriptionChange();
  const [isConfirmOpen, setConfirmOpen] = useState(false);

  if (isLoading) {
    return <Skeleton className="h-64 w-full" />;
  }

  const kind: 'module' | 'addon' = sku.startsWith('module.')
    ? 'module'
    : 'addon';
  const displayName = titleOverride ?? name;

  const handleConfirm = async () => {
    if (!user?.businessId) {
      toast.error(t(i18n.COMMON.ERROR));
      return;
    }
    try {
      await submit.mutateAsync({
        businessId: user.businessId,
        items: [{ itemKind: kind, action: 'add', targetCode: sku }],
        summary: `Add ${sku} from paywall`,
      });
      toast.success(t(i18n.MODULE_PAYWALL.SUCCESS_TOAST));
      setConfirmOpen(false);
    } catch (err) {
      toast.error(
        (err as Error)?.message ?? t(i18n.MODULE_PAYWALL.ERROR_TOAST)
      );
    }
  };

  const formatCurrencyString = (amount: number): string =>
    formatToCurrencyString(amount, currency);

  return (
    <>
      <Card className="max-w-3xl mx-auto">
        <CardContent className="p-8 text-center space-y-5">
          <div className="mx-auto inline-flex h-16 w-16 items-center justify-center rounded-full bg-brand-primary/10">
            <Lock className="h-8 w-8 text-brand-primary" />
          </div>

          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-foreground">
              {displayName}
            </h2>
            <p className="mt-2 text-sm text-gray-600 dark:text-muted-foreground">
              {pitch ?? description ?? t(i18n.MODULE_PAYWALL.DEFAULT_PITCH)}
            </p>
          </div>

          {features && features.length > 0 && (
            <ul className="mx-auto max-w-md space-y-2 text-left text-sm text-gray-700 dark:text-foreground">
              {features.map((f) => (
                <li key={f} className="flex items-start gap-2">
                  <Sparkles className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand-primary" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          )}

          <div className="rounded-lg border border-gray-200 dark:border-border bg-gray-50 dark:bg-muted p-4">
            <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-muted-foreground">
              {t(i18n.MODULE_PAYWALL.PRICE_LABEL)}
            </div>
            <div className="mt-1 text-3xl font-bold text-brand-primary tabular-nums">
              {formatCurrencyString(monthlyPriceUsd)}
              <span className="ml-1 text-sm font-normal text-gray-500 dark:text-muted-foreground">
                /{t(i18n.MODULE_PAYWALL.PER_MONTH_SHORT)}
              </span>
            </div>
          </div>

          <Button
            size="lg"
            onClick={() => setConfirmOpen(true)}
            className="w-full sm:w-auto"
          >
            {t(i18n.MODULE_PAYWALL.ADD_CTA)}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>

          <p className="text-xs text-gray-500 dark:text-muted-foreground">
            {t(i18n.MODULE_PAYWALL.BILLING_NOTE)}
          </p>
        </CardContent>
      </Card>

      <ModalDialog
        open={isConfirmOpen}
        onOpenChange={setConfirmOpen}
        title={t(i18n.MODULE_PAYWALL.CONFIRM_TITLE)}
        description={t(i18n.MODULE_PAYWALL.CONFIRM_DESCRIPTION, {
          name: displayName,
          amount: formatCurrencyString(monthlyPriceUsd),
        } as Record<string, unknown>)}
        footer={
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setConfirmOpen(false)}
              disabled={submit.isPending}
            >
              {t(i18n.COMMON.CANCEL)}
            </Button>
            <Button onClick={handleConfirm} disabled={submit.isPending}>
              {submit.isPending
                ? t(i18n.COMMON.SAVING)
                : t(i18n.MODULE_PAYWALL.CONFIRM_CTA)}
            </Button>
          </div>
        }
      />
    </>
  );
}
